import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from '@openng/optimus-ui/select';
import { Subject, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../common/BaseComponent';
import { GlobalTemplateService } from '../../../../../services/global-template.service';
import { ToastBarService } from '../../../../../services/toast-bar.service';
import { VariableMaxLengthService } from '../../../../../services/variable-max-length.service';
import {
  MetaSingleImageCtaTextInitModel,
  MetaSingleImageCtaTextInitVariables,
  MetaSingleImageCtaTextModel,
  MetaSingleImageCtaTextVariables,
} from '../../../../../models/meta-template/meta-single-image-cta-text.interface';
import { META_CTA_TEXTS } from '../../../../../utils/meta-cta-helper';
import { Helper } from '../../../../../utils/helper';

/**
 * Picks the call to action printed on the Meta single-image ad. The approved
 * label is stored verbatim under `Text`, which the previews and the docx export read.
 */
@Component({
  selector: 'app-meta-single-image-cta-text',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule],
  templateUrl: './meta-single-image-cta-text.component.html',
  styleUrl: './meta-single-image-cta-text.component.scss',
})
export class MetaSingleImageCtaTextComponent extends BaseComponent implements OnInit, OnDestroy {
  //@ input
  @Input() block: MetaSingleImageCtaTextModel = new MetaSingleImageCtaTextInitModel();
  @Input() isPreview = false;
  //@ output
  @Output() blockData = new EventEmitter<any>(); // Event emitter to send data to the parent
  @Output() removeBlockEvent = new EventEmitter<string | null>(); // New EventEmitter for remove

  // Copied, not aliased: p-select's [options] is typed as a mutable any[],
  // which a readonly array cannot satisfy under strictTemplates.
  readonly ctaTexts: string[] = [...META_CTA_TEXTS];
  selectedCtaText: string = '';
  disabled: boolean = true;
  private _destroy$ = new Subject<void>();

  constructor(
    toastBarService: ToastBarService,
    private globalTemplateService: GlobalTemplateService,
    private _variableMaxLengthService: VariableMaxLengthService
  ) {
    super(toastBarService);
  }

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new MetaSingleImageCtaTextInitVariables();
    }

    // Preselect the saved CTA, but only when it is still one of the approved labels.
    const savedCtaText = this.block.variables!['Text'] ?? '';
    this.selectedCtaText = this.ctaTexts.includes(savedCtaText) ? savedCtaText : '';

    // Disable the dropdown when the template is read-only or locked by another user.
    this.globalTemplateService
      .getIsDisabled()
      .pipe(takeUntil(this._destroy$))
      .subscribe((isDisabled: boolean) => {
        this.disabled = isDisabled;
      });

    // Register the Text max length so the docx export prints a value (not "Undefined").
    this._variableMaxLengthService.addSingleVariableMaximumLength({
      name: 'Text',
      maxLength: 4000,
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  //@ placeholders
  ctaPlaceholder: string = 'Odaberi CTA iz padajuće liste';

  //@remove
  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId); // Emit the ID to parent
  }

  //@onChange
  onChange(value: any, fieldName: keyof MetaSingleImageCtaTextVariables) {
    if (this.block.variables) {
      this.block.variables[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }
}
